import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getBusinessHours, listHolidays } from "@/lib/api";

const DAY_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const toMin = (t) => {
  const [h, m] = String(t || "").split(":").map(Number);
  return Number.isFinite(h) ? h * 60 + (m || 0) : null;
};

export const fmt12 = (t) => {
  const mins = toMin(t);
  if (mins == null) return t;
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  const h12 = h % 12 || 12;
  return `${h12}${m ? ":" + String(m).padStart(2, "0") : ""} ${h < 12 ? "AM" : "PM"}`;
};

// Returns { state: "open"|"closed"|"holiday", label, sub }
export function hoursStatus(hours, holidays, now = new Date()) {
  const byDay = Object.fromEntries((hours || []).map((h) => [String(h.day).toLowerCase(), h]));
  const holi = Object.fromEntries((holidays || []).map((h) => [h.date, h]));
  const dayAt = (offset) => {
    const d = new Date(now);
    d.setDate(d.getDate() + offset);
    return { d, key: DAY_KEYS[d.getDay()], h: byDay[DAY_KEYS[d.getDay()]], holiday: holi[ymd(d)] };
  };
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const isOpenDay = (x) => x.h && x.h.open && !x.holiday && toMin(x.h.from) != null && toMin(x.h.to) != null;

  // Overnight spill from yesterday (e.g. 18:00 - 02:00)
  const y = dayAt(-1);
  if (isOpenDay(y) && toMin(y.h.to) <= toMin(y.h.from) && nowMin < toMin(y.h.to)) {
    return { state: "open", label: "Open now", sub: `Closes at ${fmt12(y.h.to)}` };
  }

  const t = dayAt(0);
  if (isOpenDay(t)) {
    const from = toMin(t.h.from);
    const to = toMin(t.h.to);
    const overnight = to <= from;
    if (nowMin >= from && (overnight || nowMin < to)) {
      return { state: "open", label: "Open now", sub: `Closes at ${fmt12(t.h.to)}` };
    }
    if (nowMin < from) return { state: "closed", label: "Closed now", sub: `Opens at ${fmt12(t.h.from)}` };
  }

  const base = t.holiday
    ? { state: "holiday", label: "Holiday — closed today", note: t.holiday.note }
    : { state: "closed", label: isOpenDay(t) ? "Closed now" : "Closed today" };
  for (let i = 1; i <= 14; i++) {
    const n = dayAt(i);
    if (isOpenDay(n)) {
      const when = i === 1 ? "tomorrow" : n.d.toLocaleDateString("en-IN", { weekday: "short" });
      return { ...base, sub: `Opens ${when} ${fmt12(n.h.from)}` };
    }
  }
  return { ...base, sub: "No opening hours set" };
}

export function useHoursStatus() {
  const [now, setNow] = useState(new Date());
  const { data: hrs } = useQuery({
    queryKey: ["business-hours"],
    queryFn: async () => (await getBusinessHours()).data,
    retry: false,
    staleTime: 300000,
  });
  const { data: holidays } = useQuery({
    queryKey: ["holidays"],
    queryFn: async () => (await listHolidays()).data,
    retry: false,
    staleTime: 300000,
  });
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  if (!hrs?.hours) {
    const h = (holidays || []).find((x) => x.date === ymd(now));
    return h ? { state: "holiday", label: "Holiday — closed today", note: h.note } : null;
  }
  return hoursStatus(hrs.hours, holidays, now);
}
